/**
 * AdminQuickBracketEntriesPage — standalone window for Admin Quick Bracket Entries.
 *
 * Opened via window.open() from AdminSideActionDashboard so the user can
 * run Quick Entries and Quick Bracket Entries side-by-side in a split screen.
 *
 * URL: /admin/quick-bracket-entries/:leagueId/:squadId
 *
 * After saving, broadcasts on the "admin-entries-refresh" BroadcastChannel
 * so the main window knows to reload its data, then closes this window.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminQuickBracketEntriesModal from './AdminQuickBracketEntriesModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface AdminSideEvent {
  id: number;
  name: string;
  type: string;
  cost: number;
  entry_count: number;
  bracket_size?: number;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
}

const broadcast = (leagueId: string, squadId: string) => {
  try {
    const ch = new BroadcastChannel('admin-entries-refresh');
    ch.postMessage({ leagueId, squadId });
    ch.close();
  } catch { /* BroadcastChannel not supported */ }
};

const AdminQuickBracketEntriesPage: React.FC = () => {
  const { leagueId, squadId } = useParams<{ leagueId: string; squadId: string }>();
  const [sideEvents, setSideEvents] = useState<AdminSideEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!leagueId || !squadId) return;
    fetch(`${API}/admin/leagues/${leagueId}/side-events/by-squad?squadId=${squadId}`, { headers: authHeader() })
      .then(r => {
        if (!r.ok) throw new Error('Failed to load');
        return r.json();
      })
      .then((list: AdminSideEvent[]) => setSideEvents(list.filter(se => se.type === 'bracket')))
      .catch(() => setError('Failed to load side events. Make sure you are logged in as an admin.'))
      .finally(() => setLoading(false));
  }, [leagueId, squadId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-center p-8">
        <div>
          <p className="text-red-400 mb-3">{error}</p>
          <button onClick={() => window.close()} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminQuickBracketEntriesModal
      leagueId={Number(leagueId)}
      squadId={Number(squadId)}
      sideEvents={sideEvents}
      standalone
      onClose={() => window.close()}
      onRefresh={() => {
        broadcast(leagueId!, squadId!);
        window.close();
      }}
    />
  );
};

export default AdminQuickBracketEntriesPage;
